import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useOutletContext, useParams } from 'react-router-dom'

import { StateContainer } from '@/components/ui/StateContainer'
import { getDealerBillingKpis } from '@/services/billing.service'
import { UserStoreContext } from '@/stores/UserStore'
import { setDealerGroup } from '@/utils/analytics'
import { formatCurrency, formatMonthName } from '@/utils/formatters'

import CreditMemos from './CreditMemos'

const Billing = () => {
  const { id } = useParams()
  const { isWpcUser } = useContext(UserStoreContext)
  const { dealer, error: outletError } = useOutletContext()

  // State
  const [error, setError] = useState(null)
  const [firstLoad, setFirstLoad] = useState(true)
  const [, setLoadingKpis] = useState(true)
  const [kpis, setKpis] = useState({})

  const fetchKpis = async (silent = false) => {
    let handleLoading = setLoadingKpis
    if (silent) handleLoading = () => {}

    const kpisData = await getDealerBillingKpis(id, handleLoading, setError)
    setKpis(kpisData)
  }

  useEffect(() => {
    if (id) {
      const loadKpis = async () => {
        const kpisData = await getDealerBillingKpis(id, setLoadingKpis, setError)
        setKpis(kpisData)
        setFirstLoad(false)
      }
      loadKpis()
    }
  }, [id])

  useEffect(() => {
    if (dealer) setDealerGroup(dealer)
  }, [dealer])

  // Restrict billing to WPC users
  if (!isWpcUser) {
    return <Navigate to={`/dealer/${id}/detail`} replace />
  }

  const currentMonth = formatMonthName(kpis.currentMonth)
  const previousMonth = formatMonthName(kpis.previousMonth)

  return (
    <>
      <Helmet>
        <title>{dealer?.name ? `Billing - ${dealer.name}` : 'Billing Details'}</title>
      </Helmet>

      <StateContainer error={error || outletError} loading={firstLoad}>
        <div className="flex w-full flex-col overflow-y-auto pb-12">
          <div className="grid flex-none grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            <div className="bg-midnight col-span-1 row-span-1 flex flex-col items-center justify-center rounded-lg px-3 pb-2 pt-3 sm:col-span-1 md:col-span-1 md:row-span-1">
              <span className="text-brownGray text-xs uppercase leading-4 tracking-[1.5px]">
                {currentMonth} Running Total
              </span>
              <span className="text-brownGray text-lg font-semibold leading-8 tracking-[-1px]">
                {formatCurrency(kpis.currentMonthTotal)}
              </span>
            </div>
            <div className="bg-midnight col-span-1 row-span-1 flex flex-col items-center justify-center rounded-lg px-3 pb-2 pt-3 sm:col-span-1 md:col-span-1 md:row-span-1">
              <span className="text-brownGray text-xs uppercase leading-4 tracking-[1.5px]">
                {previousMonth} Total
              </span>
              <span className="text-brownGray text-lg font-semibold leading-8 tracking-[-1px]">
                {formatCurrency(kpis.previousMonthTotal)}
              </span>
            </div>
            <div className="bg-midnight col-span-1 row-span-1 flex flex-col items-center justify-center rounded-lg px-3 pb-2 pt-3 sm:col-span-1 md:col-span-1 md:row-span-1">
              <span className="text-brownGray text-xs uppercase leading-4 tracking-[1.5px]">
                YTD Through {previousMonth}
              </span>
              <span className="text-brownGray text-lg font-semibold leading-8 tracking-[-1px]">
                {formatCurrency(kpis.ytdThruPrevMonth)}
              </span>
            </div>
          </div>
          <CreditMemos dealerId={id} fetchKpis={fetchKpis} />
        </div>
      </StateContainer>
    </>
  )
}

export default Billing
